import { useState } from "react";
import { Phone, MapPin, Clock, Mail, CheckCircle, Send } from "lucide-react";
import axios from "axios";
import { usePageMeta } from "../hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  { icon: Phone, label: "Phone", value: "(701) 721-7993", href: "tel:+17017217993" },
  { icon: Mail, label: "Email", value: "office@viallbusiness.com", href: "mailto:office@viallbusiness.com" },
  { icon: MapPin, label: "Address", value: "315 Main St S Suite 308\nMinot, ND 58701", href: null },
  { icon: Clock, label: "Business Hours", value: "Mon to Fri: 9AM to 5PM\nSat to Sun: Closed", href: null },
];

const initialForm = { name: "", email: "", phone: "", business_name: "", message: "" };

// Sub-components
const ContactInfoPanel = () => (
  <div className="lg:col-span-2 space-y-8">
    <div>
      <h2 className="font-bold text-2xl text-[#1A2744] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Get in Touch</h2>
      <div className="space-y-5">
        {contactInfo.map((item) => (
          <div key={item.label} className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-[#EAF9F9] rounded-xl flex items-center justify-center flex-shrink-0">
              <item.icon size={18} className="text-[#2EC4C4]" />
            </div>
            <div>
              <p className="font-semibold text-[#1A2744] text-sm">{item.label}</p>
              {item.href
                ? <a href={item.href} className="text-[#475569] hover:text-[#2EC4C4] transition-colors text-sm">{item.value}</a>
                : <p className="text-[#475569] text-sm whitespace-pre-line">{item.value}</p>
              }
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
      <iframe
        src="https://maps.google.com/maps?q=315+Main+St+S+Suite+308,+Minot,+ND+58701&output=embed"
        width="100%" height="280" style={{ border: 0 }}
        allowFullScreen loading="lazy"
        title="Viall Business Solutions Location"
        data-testid="google-map"
      />
    </div>
  </div>
);

const SuccessState = ({ onReset }) => (
  <div data-testid="success-message" className="text-center py-12">
    <div className="w-16 h-16 bg-[#EAF9F9] rounded-full flex items-center justify-center mx-auto mb-4">
      <CheckCircle size={32} className="text-[#2EC4C4]" />
    </div>
    <h3 className="font-bold text-xl text-[#1A2744] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Message Sent!</h3>
    <p className="text-[#475569] mb-4">Thank you for reaching out. We'll be in touch within 1 business day.</p>
    <button data-testid="send-another-btn" onClick={onReset} className="text-[#2EC4C4] font-semibold hover:underline text-sm">
      Send another message
    </button>
  </div>
);

const inputClass = "w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-[#0F172A] placeholder-gray-400 focus:outline-none focus:border-[#2EC4C4] focus:ring-1 focus:ring-[#2EC4C4] transition-colors";

const ContactFormSection = ({ form, status, errorMsg, onChange, onSubmit }) => (
  <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Full Name <span className="text-[#2EC4C4]">*</span></label>
        <input type="text" name="name" required value={form.name} onChange={onChange} data-testid="input-name" placeholder="John Smith" className={inputClass} />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Email Address <span className="text-[#2EC4C4]">*</span></label>
        <input type="email" name="email" required value={form.email} onChange={onChange} data-testid="input-email" placeholder="john@business.com" className={inputClass} />
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Phone Number</label>
        <input type="tel" name="phone" value={form.phone} onChange={onChange} data-testid="input-phone" placeholder="(555) 000-0000" className={inputClass} />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Business Name</label>
        <input type="text" name="business_name" value={form.business_name} onChange={onChange} data-testid="input-business" placeholder="Your Company LLC" className={inputClass} />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Message <span className="text-[#2EC4C4]">*</span></label>
      <textarea name="message" required rows={5} value={form.message} onChange={onChange} data-testid="input-message" placeholder="Tell us about your business and what you're looking for..." className={`${inputClass} resize-none`} />
    </div>
    {status === "error" && (
      <p data-testid="error-message" className="text-red-600 text-sm bg-red-50 px-4 py-3 rounded-lg">{errorMsg}</p>
    )}
    <button type="submit" disabled={status === "loading"} data-testid="submit-contact-btn" className="w-full bg-[#2EC4C4] hover:bg-[#25A0A0] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-8 py-4 text-base transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2 active:scale-[0.99]">
      {status === "loading" ? <span>Sending...</span> : <><Send size={18} /><span>Send Message</span></>}
    </button>
  </form>
);

export default function ContactPage() {
  usePageMeta({
    title: "Contact Us | Free Quote for Employee Benefits in Minot, ND",
    description: "Get in touch with Viall Business Solutions for a free employee benefits quote. Located in Minot, ND. Call (701) 721-7993 or email office@viallbusiness.com.",
    path: "/contact",
  });
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(initialForm);
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please call us directly at (701) 721-7993.");
    }
  };

  return (
    <div className="pt-16 lg:pt-20">
      <section data-testid="contact-hero" className="bg-[#1A2744] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-bold text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Contact Us</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">Ready to get started? Reach out and we'll put together the right benefits package for your business.</p>
        </div>
      </section>

      <section data-testid="contact-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <ContactInfoPanel />
            <div className="lg:col-span-3 bg-[#F8FAFC] rounded-2xl p-8 border border-gray-100">
              <h2 className="font-bold text-2xl text-[#1A2744] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Request a Free Quote</h2>
              <p className="text-[#475569] text-sm mb-6">Fill out the form below and we'll get back to you within 1 business day.</p>
              {status === "success"
                ? <SuccessState onReset={() => setStatus(null)} />
                : <ContactFormSection form={form} status={status} errorMsg={errorMsg} onChange={handleChange} onSubmit={handleSubmit} />
              }
            </div>
          </div>
        </div>
      </section>

      <a href="tel:+17017217993" data-testid="contact-call-fab" className="fixed bottom-6 right-6 lg:hidden bg-[#2EC4C4] text-white rounded-full p-4 shadow-xl hover:bg-[#25A0A0] transition-colors z-40" aria-label="Call us">
        <Phone size={24} />
      </a>
    </div>
  );
}
